import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Shield, AlertCircle, CheckCircle2 } from 'lucide-react';
import { countryCodes } from '../mock';
import { validateForm, sanitizePhoneInput } from '../utils/validation';
import { useToast } from '../hooks/use-toast';
import { getGeolocation, submitLead, getDialCodeFromCountryCode } from '../services/apiService';

const RegistrationForm = ({ title = "Register Your Trademark for Only $45", subtitle = "Protect your brand with trusted expertise" }) => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    countryCode: '+1',
    country: 'US',
    phone: '',
    description: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [geoLocationData, setGeoLocationData] = useState(null);

  // Fetch geolocation on component mount
  useEffect(() => {
    const fetchGeolocation = async () => {
      const geoData = await getGeolocation();
      setGeoLocationData(geoData);
      
      if (geoData.success && geoData.countryCode) {
        const dialCode = getDialCodeFromCountryCode(geoData.countryCode);
        setFormData(prev => ({
          ...prev,
          countryCode: dialCode,
          country: geoData.countryCode
        }));
      }
    };

    fetchGeolocation();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    let sanitizedValue = value;
    
    // Sanitize phone input - only allow digits
    if (name === 'phone') {
      sanitizedValue = sanitizePhoneInput(value);
      // Limit to 10 digits
      if (sanitizedValue.length > 10) {
        sanitizedValue = sanitizedValue.slice(0, 10);
      }
    }
    
    setFormData(prev => ({
      ...prev,
      [name]: sanitizedValue
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleCountryCodeChange = (value) => {
    // Extract country code from dial code
    const selectedCountry = countryCodes.find(c => c.code === value);
    setFormData(prev => ({
      ...prev,
      countryCode: value,
      country: selectedCountry?.country || 'US'
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate form
    const validationErrors = validateForm(formData);
    
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      toast({
        title: "Validation Error",
        description: "Please fix the errors in the form.",
        variant: "destructive"
      });
      return;
    }

    setIsSubmitting(true);
    
    // Prepare data for CRM API
    const leadData = {
      name: formData.name,
      email: formData.email,
      countryDialCode: formData.countryCode || '+1', // Default to +1 if not set
      country: formData.country || 'US', // Default to US if not set
      phoneNumber: formData.phone, // Only numeric
      description: formData.description
    };

    console.log('Submitting lead data:', leadData);

    // Submit to CRM API via apiService
    const result = await submitLead(leadData, geoLocationData);

    console.log('Lead submission result:', result);

    setIsSubmitting(false);

    if (result.success) {
      console.log('Redirecting to:', result.redirectUrl);
      console.log('Customer ID:', result.customerId);
      
      toast({
        title: "Success!",
        description: "Registration submitted successfully. Redirecting..."
      });
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        countryCode: '+1',
        country: 'US',
        phone: '',
        description: ''
      });
      
      setIsSuccess(true);
      
      // Redirect to application page with customer ID
      if (result.redirectUrl) {
        setTimeout(() => {
          console.log('Executing redirect to:', result.redirectUrl);
          window.location.href = result.redirectUrl;
        }, 1500);
      } else {
        console.error('No redirect URL in result:', result);
        toast({
          title: "Error",
          description: "Redirect URL not found. Please contact support.",
          variant: "destructive"
        });
      }
    } else {
      // Show error message
      console.error('Submission failed:', result.error);
      toast({
        title: "Error",
        description: result.error || "Failed to submit. Please try again.",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="bg-slate-900 rounded-xl shadow-2xl shadow-[#fd5a27]/30 p-4 sm:p-6 max-w-md w-full border border-[#fd5a27]/20">
      <div className="flex justify-center mb-3">
        <div className="w-12 h-12 bg-[#fd5a27]/20 rounded-full flex items-center justify-center">
          <Shield className="w-6 h-6 text-[#fd5a27]" />
        </div>
      </div>
      
      <h3 className="text-lg sm:text-xl font-bold text-center mb-1">{title}</h3>
      <p className="text-gray-300 text-center text-xs sm:text-sm mb-4">{subtitle}</p>
      
      <form onSubmit={handleSubmit} className="space-y-3">
        {/* Name Field */}
        <div>
          <Label htmlFor="name" className="flex items-center mb-1 text-xs sm:text-sm">
            <span>Name</span>
            <span className="text-red-500 ml-1">*</span>
          </Label>
          <Input
            id="name"
            name="name"
            type="text"
            placeholder="Enter your full name"
            value={formData.name}
            onChange={handleInputChange}
            className={`h-9 text-sm ${errors.name ? 'border-red-500' : 'border-slate-600 focus:border-[#fd5a27] bg-slate-800/50'}`}
          />
          {errors.name && (
            <p className="text-red-500 text-xs mt-0.5 flex items-center">
              <AlertCircle className="w-3 h-3 mr-1" />
              {errors.name}
            </p>
          )}
        </div>

        {/* Email Field */}
        <div>
          <Label htmlFor="email" className="flex items-center mb-1 text-xs sm:text-sm">
            <span>Email</span>
            <span className="text-red-500 ml-1">*</span>
          </Label>
          <Input
            id="email"
            name="email"
            type="email"
            placeholder="Enter your email address"
            value={formData.email}
            onChange={handleInputChange}
            className={`h-9 text-sm ${errors.email ? 'border-red-500' : 'border-slate-600 focus:border-[#fd5a27] bg-slate-800/50'}`}
          />
          {errors.email && (
            <p className="text-red-500 text-xs mt-0.5 flex items-center">
              <AlertCircle className="w-3 h-3 mr-1" />
              {errors.email}
            </p>
          )}
        </div>

        {/* Phone Field */}
        <div>
          <Label htmlFor="phone" className="flex items-center mb-1 text-xs sm:text-sm">
            <span>Contact Number</span>
            <span className="text-red-500 ml-1">*</span>
          </Label>
          <div className="flex gap-2">
            <Select value={formData.countryCode} onValueChange={handleCountryCodeChange}>
              <SelectTrigger className="w-24 h-9 text-sm">
                <SelectValue placeholder="🇺🇸 +1" />
              </SelectTrigger>
              <SelectContent>
                {countryCodes.map((country) => (
                  <SelectItem key={`${country.code}-${country.country}`} value={country.code}>
                    {country.flag} {country.code}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Input
              id="phone"
              name="phone"
              type="tel"
              placeholder="1234567890 (10 digits)"
              value={formData.phone}
              onChange={handleInputChange}
              maxLength="10"
              inputMode="numeric"
              pattern="[0-9]*"
              className={`flex-1 h-9 text-sm ${errors.phone ? 'border-red-500' : 'border-slate-600 focus:border-[#fd5a27] bg-slate-800/50'}`}
            />
          </div>
          {errors.phone && (
            <p className="text-red-500 text-xs mt-0.5 flex items-center">
              <AlertCircle className="w-3 h-3 mr-1" />
              {errors.phone}
            </p>
          )}
        </div>

        {/* Description Field */}
        <div>
          <Label htmlFor="description" className="flex items-center mb-1 text-xs sm:text-sm">
            <span>Description</span>
          </Label>
          <Textarea
            id="description"
            name="description"
            placeholder="Tell us about your trademark needs..."
            value={formData.description}
            onChange={handleInputChange}
            className={`min-h-[60px] text-sm ${errors.description ? 'border-red-500' : 'border-slate-600 focus:border-[#fd5a27] bg-slate-800/50'}`}
          />
          {errors.description && (
            <p className="text-red-500 text-xs mt-0.5 flex items-center">
              <AlertCircle className="w-3 h-3 mr-1" />
              {errors.description}
            </p>
          )}
        </div>

        {/* Submit Button */}
        <Button
          type="submit"
          className="w-full bg-gradient-to-r from-[#fd5a27] to-[#e04a1f] hover:from-amber-500 hover:to-amber-600 text-white font-semibold py-2.5 text-sm transition-all duration-300"
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <span className="flex items-center justify-center">
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              Processing...
            </span>
          ) : isSuccess ? (
            <span className="flex items-center justify-center">
              <CheckCircle2 className="mr-2 h-5 w-5" />
              Success!
            </span>
          ) : (
            'Start Your Registration'
          )}
        </Button>

        {/* Terms */}
        <p className="text-[10px] sm:text-xs text-gray-500 text-center mt-2">
          By submitting, you agree to our{' '}
          <a href="/terms-conditions" className="text-[#fd5a27] hover:underline">
            Terms
          </a>{' '}
          and{' '}
          <a href="/privacy-policy" className="text-[#fd5a27] hover:underline">
            Privacy Policy
          </a>
        </p>
      </form>
    </div>
  );
};

export default RegistrationForm;
